/**
 * SyntheticBadge - chart-header / list-row / export safety marker (PROD-569).
 *
 * Renders the verbatim copy `SYNTHETIC PATIENT - DEMO ONLY` (hyphen, never
 * em dash per HC5) in the Signal DS warning palette. This is mitigation 1
 * of the ADR-PROTO-002 4-mitigation stack: orange / red strong-contrast
 * pill that is unmissable on every patient view, list row, chart header,
 * and exported view.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §4 (SyntheticPatientBadge):
 *   - Badge container: Component (Badge variant `warning-strong`)
 *     [GAP: signal-ds-package] - tracked as G6 in
 *     docs/SIGNAL-DS-CONTRIBUTIONS.md until Signal DS publishes the
 *     canonical Badge variant.
 *   - Warning palette: Token color (`warning-strong`) - DS-existing via
 *     src/styles/tokens.ts (G6 also covers the token contribution).
 *
 * HC2: every color, spacing, radius value resolves through
 * src/styles/tokens.ts (no raw hex). HC5: zero em dashes anywhere in the
 * rendered copy or in this comment block. HC6 a11y: the badge is
 * `role="status" aria-live="polite"` so screen readers announce its
 * presence; this is the canonical synthetic-identity announcement and the
 * inline `<PatientNameTag>` `[SYNTHETIC]` tag is hidden from screen
 * readers (`aria-hidden="true"`) to avoid double-reading on every name.
 *
 * Verification: tests/synthetic-badge.test.tsx covers SPB-1 through
 * SPB-4 (verbatim copy, warning palette token classes, every size
 * variant renders, role/aria assertions).
 */
export interface SyntheticBadgeProps {
  /**
   * Size variant per COMPONENT-CONTRACTS §4 SPB-3. Defaults to `header`
   * (chart-header size). `inline-row` shrinks to fit a list row; `export`
   * is print-safe (no animation, high-contrast palette).
   */
  variant?: 'header' | 'inline-row' | 'export';
  className?: string;
}

const VARIANT_CLASSES: Record<NonNullable<SyntheticBadgeProps['variant']>, string> = {
  // Chart-header: prominent pill with ring + icon dot.
  header:
    'gap-gutter-sm px-gutter-md py-1 text-signal-sm tracking-wide',
  // Inline list-row: compact pill that fits beside a TLP badge.
  'inline-row':
    'gap-1 px-gutter-sm py-0.5 text-signal-xs tracking-wide',
  // Export / print: high-contrast, no animations, slightly heavier weight.
  export:
    'gap-gutter-sm px-gutter-md py-1 text-signal-sm tracking-wider',
};

/** Verbatim badge copy - HC5 hyphen, never em dash. */
export const SYNTHETIC_BADGE_TEXT = 'SYNTHETIC PATIENT - DEMO ONLY';

export function SyntheticBadge({ variant = 'header', className }: SyntheticBadgeProps) {
  const baseClasses =
    'inline-flex items-center rounded-signal-pill bg-warning-strong-bg font-bold uppercase text-warning-strong-fg ring-1 ring-inset ring-warning-strong';
  const composed = [baseClasses, VARIANT_CLASSES[variant], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span
      role="status"
      aria-live="polite"
      data-synthetic-badge={variant}
      className={composed}
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-signal-pill bg-warning-strong"
      />
      {SYNTHETIC_BADGE_TEXT}
    </span>
  );
}
